import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { displayAmount, formatDateOnly, errorWords } from "@alm/domain";
import { PageHeader } from "@/components/app/shell/page-header";
import { BilledResident } from "@/components/app/payments/billed-resident";
import { Balance } from "@/components/app/payments/balance";
import { DueDate } from "@/components/app/payments/due-date";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { ReportExport } from "@/components/app/documents/report-export";
import { ReportTable, type ReportColumn } from "@/components/app/documents/report-table";
import {
  periodDescription,
  periodInput,
  periodReady,
  ReportPeriodPicker,
  todayPeriod,
  type ReportPeriod,
} from "@/components/app/documents/report-period";
import { StatTile } from "@/components/app/lists/stat-tile";
import { StatTiles } from "@/components/app/lists/stat-tiles";
import { PersonCell } from "@/components/app/patterns/person-cell";
import { YearOverviewGrid } from "@/components/app/lists/year-overview-grid";
import { Badge } from "@/components/ui/badge";
import { saveFile } from "@/lib/files/download";
import { useFacility } from "@/lib/facility/facility";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { orpc, orpcClient, type Output } from "@/lib/orpc";
import {
  periodFromSearch,
  periodSearch,
  periodToSearch,
  type PeriodSearch,
} from "@/lib/facility/report-period-search";
import { optionalString, KEEP_PLACE, optionalSearch } from "@/lib/search";
import { useFeature } from "@/lib/policy";
import {
  keepResident,
  ResidentFilters,
  type ResidentFilterState,
} from "./-components/resident-filters";
import { RevenueBreakdown } from "./-components/revenue-breakdown";
import { linkClass } from "@/components/app/lists/record-link-shared";
import { cn } from "@/lib/utils";

type ArSearch = PeriodSearch & { q?: string; payment?: string; resident?: string };

export const Route = createFileRoute("/_authenticated/_financials/ar-summary/")({
  validateSearch: (search: Record<string, unknown>): ArSearch => ({
    ...periodSearch(search),
    q: optionalSearch(search.q),
    payment: optionalString(search.payment)?.slice(0, 40) || undefined,
    resident: optionalString(search.resident)?.slice(0, 40) || undefined,
  }),
  component: ArSummary,
});

type Aging = Output<typeof orpcClient.financials.reports.arAging>;
type AgingRow = Aging["items"][number];
type Resident = Output<typeof orpcClient.financials.reports.rentRoll>["residents"][number];

/** Hoisted: the columns close over nothing, and a table is not a component. */
const RESIDENT_COLUMNS: ReadonlyArray<ReportColumn<Resident>> = [
  {
    header: "Resident",
    cell: (row) => (
      <PersonCell
        kind="resident"
        name={`${row.lastName}, ${row.firstName}`}
        renderLink={(name) => (
          <Link
            to="/residents/$residentId"
            params={{ residentId: row.id }}
            className={cn(linkClass, "truncate")}
          >
            {name}
          </Link>
        )}
      />
    ),
    value: (row) => `${row.lastName}, ${row.firstName}`,
  },
  { header: "Room", cell: (row) => row.roomNumber || "-" },
  { header: "Monthly rent", cell: (row) => displayAmount(row.monthlyRent), align: "right" },
  { header: "Level of care", cell: (row) => displayAmount(row.levelOfCareCharge), align: "right" },
  {
    header: "Balance",
    cell: (row) => <Balance amount={row.currentBalance} pastDue={Number(row.daysOverdue) > 0} />,
    value: (row) => row.currentBalance,
    align: "right",
  },
  {
    header: "Status",
    cell: (row) => (
      <Badge
        variant={
          row.paymentStatus === "current" ? "ok" : row.paymentStatus === "partial" ? "warn" : "crit"
        }
      >
        {row.paymentStatus === "current"
          ? "Current"
          : row.paymentStatus === "partial"
            ? "Partial"
            : "Overdue"}
      </Badge>
    ),
    value: (row) => row.paymentStatus,
  },
  { header: "Days overdue", cell: (row) => row.daysOverdue, align: "right" },
  {
    header: "Last payment",
    cell: (row) =>
      row.lastPaymentDate
        ? `${row.lastPaymentAmount}, ${formatDateOnly(row.lastPaymentDate)}`
        : "-",
    value: (row) => row.lastPaymentDate ?? "",
  },
];

const INVOICE_COLUMNS: ReadonlyArray<ReportColumn<AgingRow>> = [
  {
    header: "Invoice",
    cell: (row) => (
      <Link
        to="/invoice-roll/$invoiceId"
        params={{ invoiceId: row.invoiceId }}
        className="hover:underline"
      >
        {row.invoiceNumber ?? row.invoiceId.slice(0, 8)}
      </Link>
    ),
    value: (row) => row.invoiceNumber ?? row.invoiceId,
  },
  {
    header: "Resident",
    cell: (row) => <BilledResident name={row.residentName} residentId={row.residentId} />,
    value: (row) => row.residentName,
  },
  { header: "Period", cell: (row) => row.billingPeriod },
  {
    header: "Due",
    cell: (row) => <DueDate due={row.dueDate} owing />,
    value: (row) => formatDateOnly(row.dueDate),
  },
  {
    header: "Age",
    // A pale pill in the bucket's ink: a solid red badge on every overdue row was a flood.
    cell: (row) =>
      row.bucket === "current" ? (
        <Badge variant="outline">Current</Badge>
      ) : (
        <Badge variant={row.bucket === "90+" ? "crit" : "warn"}>{`${row.bucket} days`}</Badge>
      ),
    value: (row) => row.bucket,
  },
  {
    header: "Balance",
    cell: (row) => <Balance amount={row.balance} pastDue={row.bucket !== "current"} />,
    value: (row) => row.balance,
    align: "right",
  },
];

/** The rent roll's report read for money: revenue split, balance aging and who carries them. The
 * invoice list below is where a collections call starts, since a balance needs an invoice to
 * name. */
function ArSummary() {
  const { facilityId } = useFacility();
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = (next: Partial<ArSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }), ...KEEP_PLACE });
  const fallback: ReportPeriod = { ...todayPeriod(), view: "month" };
  const period = periodFromSearch(params, fallback);
  const setPeriod = (next: ReportPeriod) => patch(periodToSearch(next, fallback));
  const { text: search, change: setSearch } = useSearchParamText(params.q, (q) => patch({ q }));
  const filters: ResidentFilterState = {
    search,
    paymentStatus: params.payment ?? "all",
    residentStatus: params.resident ?? "all",
  };
  const setFilters = (next: ResidentFilterState) => {
    if (next.search !== search) setSearch(next.search);
    patch({
      payment: next.paymentStatus === "all" ? undefined : next.paymentStatus,
      resident: next.residentStatus === "all" ? undefined : next.residentStatus,
    });
  };

  const input = { facilityId, ...periodInput(period) };
  const report = useQuery({
    ...orpc.financials.reports.rentRoll.queryOptions({ input }),
    enabled: periodReady(period),
  });
  // The invoice list is the collections report's, and goes with that feature.
  const collections = useFeature("collections");
  const aging = useQuery({
    ...orpc.financials.reports.arAging.queryOptions({ input: { facilityId } }),
    enabled: collections,
  });

  const download = useMutation(
    orpc.financials.reports.rentRollExport.mutationOptions({
      onSuccess: saveFile,
      onError: (error: Error) => toast.error(errorWords(error)),
    }),
  );

  const residents = (report.data?.residents ?? []).filter((row) => keepResident(row, filters));

  const buckets = report.data
    ? [
        { label: "Current", amount: report.data.aging.current },
        { label: "1–30 days", amount: report.data.aging.days1to30 },
        { label: "31–60 days", amount: report.data.aging.days31to60 },
        { label: "61–90 days", amount: report.data.aging.days61to90 },
        { label: "90+ days", amount: report.data.aging.days90plus },
      ]
    : [];

  return (
    <>
      <PageHeader
        title="AR summary"
        meta={
          report.data
            ? `${periodDescription(period)}, ${report.data.stats.collectionRate}% collected`
            : "What is owed, and for how long."
        }
        actions={
          <ReportExport
            labels={{ csv: "Report CSV", pdf: "Print report" }}
            pending={download.isPending}
            onExport={(format) => download.mutate({ ...input, format })}
          />
        }
      />

      <div className="space-y-1">
        <ReportPeriodPicker period={period} onChange={setPeriod} />
        <p className="text-muted-foreground text-sm">{periodDescription(period)}</p>
      </div>

      {report.data ? (
        <StatTiles>
          <StatTile label="Rent billed" value={report.data.stats.totalMonthlyRent} />
          <StatTile label="Care billed" value={report.data.stats.totalMonthlyCare} />
          <StatTile label="CBA co-pay" value={report.data.stats.totalMonthlyCbaCoPay} />
          <StatTile
            label="CBA room & board"
            value={report.data.stats.totalMonthlyCbaRoomAndBoard}
          />
          <StatTile label="CBA claim" value={report.data.stats.totalMonthlyCbaClaim} />
          <StatTile label="Outstanding" value={report.data.stats.totalOutstandingBalance} />
          <StatTile
            label="Collected"
            value={`${report.data.stats.collectionRate}%`}
            hint="Of what was billed"
          />
          <StatTile
            label="Overdue"
            value={report.data.stats.overdueCount}
            hint={`Of ${report.data.residents.length} residents`}
            tone={report.data.stats.overdueCount > 0 ? "crit" : undefined}
          />
        </StatTiles>
      ) : null}

      {report.isError ? (
        <ReadFailed
          message="The AR summary could not be loaded."
          error={report.error}
          onRetry={() => void report.refetch()}
          retrying={report.isFetching}
        />
      ) : period.view === "yearOverview" ? (
        report.data?.yearOverview ? (
          <YearOverviewGrid
            year={report.data.yearOverview.year}
            months={report.data.yearOverview.monthlyData}
            totals={report.data.yearOverview.yearTotals}
          />
        ) : null
      ) : (
        <>
          {report.data ? <RevenueBreakdown breakdown={report.data.revenueBreakdown} /> : null}

          {buckets.length ? (
            <div>
              <h2 className="mb-3 text-lg font-semibold">How old the balances are</h2>
              <StatTiles>
                {buckets.map((bucket) => (
                  <StatTile key={bucket.label} label={bucket.label} value={bucket.amount} />
                ))}
              </StatTiles>
            </div>
          ) : null}

          <div>
            <div className="mb-2 flex flex-wrap items-end gap-3">
              <h2 className="mr-auto text-lg font-semibold">Residents</h2>
              <ResidentFilters value={filters} onChange={setFilters} />
            </div>
            <ReportTable
              rows={residents}
              pending={report.isPending}
              filename="ar-residents.csv"
              noun="residents"
              rowKey={(row) => row.id}
              columns={RESIDENT_COLUMNS}
              empty={{
                title: "Nobody to show",
                description: "No resident matches these filters in this period.",
              }}
            />
          </div>

          {collections ? (
            <div>
              <h2 className="mb-3 text-lg font-semibold">
                Outstanding invoices{aging.data ? `, ${aging.data.total}` : ""}
              </h2>
              {/* A failed read is not "every invoice is settled". */}
              {aging.isError ? (
                <ReadFailed
                  message="Outstanding invoices could not be loaded."
                  error={aging.error}
                  onRetry={() => void aging.refetch()}
                  retrying={aging.isFetching}
                />
              ) : (
                <ReportTable
                  rows={aging.data?.items}
                  pending={aging.isPending}
                  filename="ar-aging.csv"
                  noun="invoices"
                  rowKey={(row) => row.invoiceId}
                  columns={INVOICE_COLUMNS}
                  empty={{ title: "Nothing outstanding", description: "Every invoice is settled." }}
                />
              )}
            </div>
          ) : null}
        </>
      )}
    </>
  );
}
